import React, {useRef} from "react"
import {useAccessToken, useIsUnitAdminUser, useLogout, useUserClaimsData} from "../../services/auth"
import {useToggle, useClickAway} from "react-use"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import Icon from "../Icon/Icon"
import classNames from "classnames"
import {NavLink} from "../NavLink/NavLink"
import ShareBoxInterfaceEssentialIcon from "./ShareBoxInterfaceEssentialIcon"
import {useModal} from "react-modal-hook"
import {SwitchBuildPathModeModal} from "../SwitchBuildPathModeModal/SwitchBuildPathModeModal"
import {useOrgBankingSetup} from "../../services/orgBankingSetup"
import {BuildPathType, isBuildPathType} from "../../pages/ChooseBuildPath/buildPathOptions"
import {updateOrgBankingSetup} from "../../resources/orgBankingSetupType"

interface NavbarUserDropdownProps {
	narrow?: boolean
}

export default function NavbarUserDropdown({narrow}: NavbarUserDropdownProps) {
	const ref = useRef(null)
	const [isDropdownActive, toggleDropdown] = useToggle(false)
	useClickAway(ref, () => toggleDropdown(false))
	const dropdownClassname = classNames("navbar-user-dropdown dropdown is-right", isDropdownActive && "is-active")
	const userTokenData = useUserClaimsData()
	const logout = useLogout()
	const orgName = userTokenData.org
	const isUnitAdmin = useIsUnitAdminUser()
	const userFirstLetter = userTokenData.sub ? userTokenData.sub[0] : "U"
	const {fetch, setup} = useOrgBankingSetup()
	const accessToken = useAccessToken()

	const [showSwitchBuildPathModal, hideSwitchBuildPathModal] = useModal(() => {
		if (!isBuildPathType(setup)) {
			return null
		}
		const onSwitch = async (selectedMode: BuildPathType) => {
			await updateOrgBankingSetup(accessToken, selectedMode)
			fetch()
		}
		return <SwitchBuildPathModeModal close={hideSwitchBuildPathModal} currentMode={setup} onSwitch={onSwitch} />
	})

	return (
		<div className={dropdownClassname} ref={ref}>
			<div className="navbar-user-dropdown-trigger" onClick={() => toggleDropdown()}>
				<div className="navbar-user-dropdown-org-info">{orgName ?? "Unit"}</div>
				<div className="navbar-user-dropdown-user-letter">{userFirstLetter}</div>
			</div>
			<div className="dropdown-menu">
				<div className="dropdown-content">
					<div className="navbar-user-dropdown-content-details">
						<div className="navbar-user-dropdown-content-user-info">
							<div className="navbar-user-dropdown-user-letter large">{userFirstLetter}</div>
							<div className="navbar-user-dropdown-content-username">{userTokenData.sub}</div>
						</div>
					</div>
					<hr className="navbar-user-dropdown-content-divider" />
					<ul className="navbar-user-dropdown-content-links" onClick={toggleDropdown}>
						{!narrow && (
							<>
								<HasPermission resource="user" action="get">
									<li>
										<NavLink end className="has-icon" activeClassName="is-active" to="/users">
											<span className="icon">
												<Icon icon="user-group" size={16} />
											</span>
											<span className="menu-item-label">Manage Users</span>
										</NavLink>
									</li>
								</HasPermission>
								{(setup === "ReadyToLaunchBanking" || setup === "Custom") && (
									<li>
										<NavLink
											to="#"
											end
											className="has-icon"
											onClick={(e) => {
												e.preventDefault()
												toggleDropdown(false)
												showSwitchBuildPathModal()
											}}
										>
											<span className="icon">
												<Icon icon="Data-Transfer-Refresh" size={16} />
											</span>
											<span className="menu-item-label">Switch Build Path</span>
										</NavLink>
									</li>
								)}
								{isUnitAdmin && (
									<li>
										<NavLink end className="has-icon" activeClassName="is-active" to="/admin">
											<span className="icon">
												<Icon icon="settings-cog-1" size={16} />
											</span>
											<span className="menu-item-label">Admin</span>
										</NavLink>
									</li>
								)}
								<hr className="navbar-user-dropdown-content-divider" />
								<li>
									<NavLink end className="has-icon" activeClassName="is-active" to="/change-password">
										<span className="icon">
											<Icon icon="security-password-lock--programing-apps-websites" size={16} />
										</span>
										<span className="menu-item-label">Change Password</span>
									</NavLink>
								</li>
							</>
						)}
						<li>
							<NavLink end className="has-icon" activeClassName="is-active" to="#" onClick={logout}>
								<span className="icon">
									<ShareBoxInterfaceEssentialIcon />
								</span>
								<span className="menu-item-label">Logout</span>
							</NavLink>
						</li>
					</ul>
				</div>
			</div>
		</div>
	)
}
